import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";

import type { Progress } from "@/application/use-cases/b2c/build-b2c-board";
import { EditableDateRange } from "@/presentation/components/b2c/editable-date-range";
import { EditableTitle } from "@/presentation/components/b2c/editable-title";
import type { StagePatch } from "@/infrastructure/repositories/b2c-board.supabase.repository";
import type { B2cStageRow } from "@/infrastructure/supabase/b2c-board.types";

/**
 * A program stage: its window, how a workstream may start in it, and its workstreams.
 * Collapsed, it keeps one line (title, window, progress and its workstream codes) so every
 * stage fits on screen; the chevron opens it.
 */
export function StageSection({
  stage,
  progress,
  workstreamCodes,
  open,
  onToggle,
  canWrite,
  onPatch,
  children,
}: {
  stage: B2cStageRow;
  progress: Progress;
  workstreamCodes: string[];
  open: boolean;
  onToggle: () => void;
  canWrite: boolean;
  onPatch: (patch: StagePatch) => void;
  children: ReactNode;
}) {
  const bodyId = `stage-${stage.key}`;
  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-controls={bodyId}
            aria-label={`${open ? "Collapse" : "Expand"} ${stage.title}`}
            className="text-muted-foreground hover:text-foreground focus-visible:ring-ring -ml-1 rounded p-0.5 focus-visible:ring-2 focus-visible:outline-none"
          >
            <ChevronRight
              className={`size-5 transition-transform motion-reduce:transition-none ${open ? "rotate-90" : ""}`}
              aria-hidden
            />
          </button>
          <h2 className="text-[19px] font-semibold tracking-tight">
            <EditableTitle
              value={stage.title}
              canWrite={canWrite}
              onChange={(title) => onPatch({ title })}
              ariaLabel="Stage title"
              className="px-0.5"
            />
          </h2>
          <EditableDateRange
            start={stage.start_date}
            end={stage.end_date}
            canWrite={canWrite}
            onChange={onPatch}
            label={stage.title}
          />
          <span className="font-data text-muted-foreground text-[12px]">
            {progress.done}/{progress.total} tasks done
          </span>
          {!open && workstreamCodes.length > 0 ? (
            <span className="font-data text-muted-foreground text-[12px]">
              {workstreamCodes.length} workstreams · {workstreamCodes.join(" ")}
            </span>
          ) : null}
        </div>
        {open && stage.summary ? (
          <p className="text-muted-foreground max-w-3xl text-[13px] leading-relaxed">
            {stage.summary}
          </p>
        ) : null}
        {open && stage.gate_rule ? (
          <p className="font-data text-[11px] tracking-wide">
            <span className="text-muted-foreground tracking-[0.14em] uppercase">Gate · </span>
            {stage.gate_rule}
          </p>
        ) : null}
      </header>
      {open ? (
        <div id={bodyId} className="flex flex-col gap-4">
          {children}
        </div>
      ) : null}
    </section>
  );
}
